/**
 * 📊 Report Builder — Dashboards
 * Lista de dashboards personalizables con widgets.
 * Ruta: /apps/reports/dashboards
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Badge,
  EmptyState,
  Divider,
  Box,
  Spinner,
  InlineGrid,
  Checkbox,
} from '@/lib/polaris';
import {
  ChartVerticalFilledIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  PlusIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';

interface Dashboard {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  is_shared: boolean;
  layout: unknown[];
  created_at: string;
  updated_at: string;
}

export default function ReportDashboardsPage() {
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Dashboard | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Dashboard | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(false);

  // Por la caché (ADR-0012, paso 3): volver a esta pantalla la pinta con lo que ya se sabe.
  const consulta = useQuery({
    queryKey: ['dashboards', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<Dashboard[]> => {
      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('is_default', { ascending: false })
        .order('name');
      if (error) throw error;
      return (data ?? []) as Dashboard[];
    },
  });

  const dashboards = useMemo(() => consulta.data ?? [], [consulta.data]);
  // 🪤 `isPending`, no `isFetching`: con la lista en pantalla, revalidar no es "cargando".
  const loading = !!tenantId && consulta.isPending;

  // 🪤 v5 sacó `onError`: avisar en el render sería un efecto en medio del render.
  useEffect(() => {
    if (consulta.error) toast.error('Error al cargar dashboards', getErrorMessage(consulta.error));
  }, [consulta.error, toast]);

  // Se llama tras crear, editar o borrar.
  const loadDashboards = useCallback(async () => { await consulta.refetch(); }, [consulta]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setIsShared(false);
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (d: Dashboard) => {
    setEditing(d);
    setName(d.name);
    setDescription(d.description ?? '');
    setIsShared(d.is_shared);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId || !name.trim()) return;
    setSaving(true);

    const row = {
      tenant_id: tenantId,
      name: name.trim(),
      description: description.trim() || null,
      is_shared: isShared,
    };

    try {
      if (editing) {
        const { error } = await supabase.from('dashboards').update(row).eq('id', editing.id);
        if (error) throw error;
        toast.success('Dashboard actualizado');
      } else {
        const { error } = await supabase.from('dashboards').insert(row);
        if (error) throw error;
        toast.success('Dashboard creado');
      }
      setDialogOpen(false);
      await loadDashboards();
    } catch (error: unknown) {
      toast.error('Error al guardar', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('dashboards').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success('Dashboard eliminado');
      setDeleteTarget(null);
      await loadDashboards();
    } catch (error: unknown) {
      toast.error('Error al eliminar', getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={ChartVerticalFilledIcon}
          title="Dashboards"
          onBack={() => storeNavigate(ROUTES.APPS_REPORTS)}
          backAccessibilityLabel="Volver a Reportes"
        />
      }
      primaryAction={{ content: 'Crear dashboard', onAction: openCreate }}
      secondaryActions={[
        { content: 'Reportes guardados', onAction: () => storeNavigate(ROUTES.APPS_REPORTS_SAVED) },
      ]}
    >
      {loading ? (
        <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
      ) : dashboards.length === 0 ? (
        <Card>
          <EmptyState
            heading="Crea tu primer dashboard"
            image=""
            action={{ content: 'Crear dashboard', onAction: openCreate }}
          >
            <Text as="p">
              Los dashboards te permiten visualizar métricas clave de ventas, CRM, emails y más
              con widgets personalizables (KPIs, gráficos, tablas).
            </Text>
          </EmptyState>
        </Card>
      ) : (
        <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="400">
          {dashboards.map((d) => (
            <Card key={d.id}>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="start">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingMd">{d.name}</Text>
                      {d.is_default && <Badge tone="info">Default</Badge>}
                      {d.is_shared && <Badge tone="success">Compartido</Badge>}
                    </InlineStack>
                    {d.description && (
                      <Text as="p" tone="subdued" variant="bodySm">{d.description}</Text>
                    )}
                  </BlockStack>
                </InlineStack>
                <Text as="p" tone="subdued" variant="bodySm">
                  {(d.layout as unknown[]).length} widgets · Actualizado {formatDate(d.updated_at)}
                </Text>
                <Divider />
                <InlineStack gap="200">
                  <Button
                    icon={ViewIcon}
                    onClick={() => storeNavigate(`/apps/reports/dashboards/${d.id}`)}
                    variant="primary"
                    size="slim"
                  >
                    Ver
                  </Button>
                  <Button icon={EditIcon} onClick={() => openEdit(d)} variant="plain" size="slim" />
                  <Button icon={DeleteIcon} onClick={() => setDeleteTarget(d)} variant="plain" tone="critical" size="slim" />
                </InlineStack>
              </BlockStack>
            </Card>
          ))}
        </InlineGrid>
      )}

      {/* Modal Crear/Editar */}
      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? 'Editar dashboard' : 'Nuevo dashboard'}
        primaryAction={{ content: 'Guardar', onAction: handleSave, loading: saving, disabled: !name.trim() }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDialogOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField label="Nombre" value={name} onChange={setName} placeholder="Ej: Dashboard de ventas" autoComplete="off" requiredIndicator />
            <TextField label="Descripción" value={description} onChange={setDescription} placeholder="Métricas principales de ventas y conversión" autoComplete="off" multiline={2} />
            <Checkbox label="Compartir con todo el equipo" checked={isShared} onChange={setIsShared} />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Modal eliminar */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar dashboard"
        primaryAction={{ content: 'Eliminar', onAction: handleDelete, loading: deleting, destructive: true }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDeleteTarget(null) }]}
      >
        <Modal.Section>
          <Text as="p">¿Eliminar el dashboard <strong>{deleteTarget?.name}</strong> y todos sus widgets?</Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
